/**
 * Fluxible actions for use in the edit flow.
 */
import type Q from 'q';

import type { ActionContext } from 'js/lib/ActionContext';

import _t from 'i18n!nls/staff-management';

import type { NotificationParam } from '../../common/notifications/Notifications';
import { EVENT_NAMES } from '../constants';
import type { IdentifiableInterface, StaffMembershipType } from '../types';
import { update } from './core';

/**
 * Prompt user to edit memberships for users.
 *
 * Calling with no args acts as an unset.
 *
 * This will trigger a modal in the flow.
 */
export const prompt = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /**
   * Users opted in for edit, particular entity id to unlink from, and type of entity.
   * Optionally a default set of selected roles may be passed in.
   */
  {
    fromId,
    type,
    roles = [],
    users = [],
  }: {
    users?: Array<IdentifiableInterface>;
    fromId?: string | number;
    roles?: Array<string>;
    type?: keyof typeof StaffMembershipType;
  } = {}
): void => {
  actionContext.dispatch(EVENT_NAMES.UI, { editFlow: { fromId, roles, type, users } });
};

/**
 * Provide a new set of memberships to the selected members.
 */
export const confirm = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** List of users from which to add memberships to, and the type of memberships. */
  {
    fromId,
    roles,
    type,
    users,
    notify,
    // API DI.
    _update = update,
  }: {
    fromId: string | number;
    roles: Array<string>;
    type: keyof typeof StaffMembershipType;
    users: Array<IdentifiableInterface>;
    _update?(
      actionContext: ActionContext,
      args: {
        fromId: string;
        roles: Array<string>;
        type: keyof typeof StaffMembershipType;
        users: Array<IdentifiableInterface>;
      }
    ): Q.Promise<void>;
    notify: (notification: NotificationParam) => void;
  }
) => {
  const editFlowState = actionContext.getStore('StaffManagementStoreV2').ui('editFlow');
  const resolvedFromId = fromId || editFlowState.fromId;
  const resolvedRoles = roles || editFlowState.roles;
  const resolvedType = type || editFlowState.type;
  const resolvedUsers = users || editFlowState.users;

  // Close the modal once we've gotten the state information we need.
  actionContext.executeAction(prompt);
  return actionContext
    .executeAction(_update, { fromId: resolvedFromId, roles: resolvedRoles, type: resolvedType, users: resolvedUsers })
    .then(() => {
      notify({
        message: users.length === 1 ? _t('Membership updated successfully.') : _t('Memberships updated successfully.'),
        type: 'success',
      });
    })
    .catch(() => {
      notify({
        message: _t('Error during membership update. Recommend refreshing your browser and trying again.'),
        type: 'error',
      });
    });
};

/** Set of roles we will potentially give to a set of users. */
export const roles = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** Ids of roles. */
  roles: Array<string> = [] // eslint-disable-line @typescript-eslint/no-shadow
): void => {
  const editFlowState = actionContext.getStore('StaffManagementStoreV2').ui('editFlow');
  actionContext.dispatch(EVENT_NAMES.UI, { editFlow: { ...editFlowState, roles } });
};

/** Set of user objects we will potentially add as members of a set of roles. */
export const users = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  users: Array<IdentifiableInterface> = [] // eslint-disable-line @typescript-eslint/no-shadow
): void => {
  const editFlowState = actionContext.getStore('StaffManagementStoreV2').ui('editFlow');
  actionContext.dispatch(EVENT_NAMES.UI, { editFlow: { ...editFlowState, users } });
};
